'use client';
import React, { useState } from 'react';
import {
  FiEdit2,
  FiTrash2,
  // FiMoreVertical,
  FiPrinter,
  FiShare2,
  // FiClock,
  FiUser,
  // FiCalendar,
  FiRefreshCw,
} from 'react-icons/fi';

import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Icon,
  Stack,
  Badge,
  SimpleGrid,
  // Separator,
  IconButton,
  Dialog,
} from '../../index';
import { formatDate } from '../../utils/date';
import { Card } from '../card';
import { LoadingState } from '../loading-state';
import { EmptyState } from '../empty-state';

export interface DetailField {
  label: string;
  value: unknown;
  type?: 'text' | 'number' | 'date' | 'datetime' | 'badge' | 'currency' | 'custom';
  format?: (value: unknown) => string;
  render?: (value: unknown) => React.ReactNode;
  gridColumn?: string;
  hideIfEmpty?: boolean;
}

export interface DetailSection {
  id: string;
  title: string;
  fields: DetailField[];
  columns?: number;
}

export interface TimelineEvent {
  id: string;
  type: string;
  title: string;
  description?: string;
  timestamp: Date | string;
  user?: {
    id: string;
    name: string;
    avatar?: string;
  };
  icon?: React.ComponentType;
  color?: string;
}

export interface RelatedItem {
  id: string;
  title: string;
  subtitle?: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ComponentType;
  badge?: string;
}

export interface ResourceDetailTab {
  id: string;
  label: string;
  content: React.ReactNode;
  badge?: string | number;
}

export interface ResourceDetailProps {
  // Resource data
  data: Record<string, unknown>;
  isLoading?: boolean;
  error?: Error | null;

  // Resource info
  resourceName: string;
  title?: string;
  subtitle?: string;

  // Status
  status?: {
    label: string;
    color?: string;
  };

  // Metadata
  metadata?: DetailSection;

  // Main content sections
  sections?: DetailSection[];

  // Tabs
  tabs?: ResourceDetailTab[];
  defaultTab?: string;

  // Timeline
  timeline?: TimelineEvent[];
  timelineTitle?: string;

  // Related items
  relatedItems?: RelatedItem[];
  relatedTitle?: string;

  // Actions
  onEdit?: () => void;
  onDelete?: () => void;
  onPrint?: () => void;
  onShare?: () => void;
  onRefresh?: () => void;
  customActions?: {
    label: string;
    icon?: React.ComponentType;
    onClick: () => void;
    variant?: 'primary' | 'outline' | 'ghost';
    colorScheme?: string;
  }[];

  // Quick edit
  enableQuickEdit?: boolean;
  quickEditFields?: string[];
  onQuickEdit?: (field: string, value: unknown) => void | Promise<void>;

  // Layout
  showHeader?: boolean;
  showSidebar?: boolean;
  sidebarPosition?: 'left' | 'right';
}

function DetailFieldComponent({ field, value }: { field: DetailField; value: unknown }) {
  if (field.hideIfEmpty && !value) return null;

  const renderValue = () => {
    if (field.render) {
      return field.render(value);
    }

    if (field.format) {
      return field.format(value);
    }

    switch (field.type) {
      case 'date':
        return value
          ? formatDate(new Date(value as string | number | Date), 'tr-TR', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          : '-';
      case 'datetime':
        return value
          ? formatDate(new Date(value as string | number | Date), 'tr-TR', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '-';
      case 'badge':
        return value ? <Badge>{String(value)}</Badge> : '-';
      case 'currency':
        return value ? `$${value.toLocaleString()}` : '-';
      default:
        return value || '-';
    }
  };

  const renderedValue = renderValue();
  const isReactElement = React.isValidElement(renderedValue);

  return (
    <Box gridColumn={field.gridColumn}>
      <Text fontSize="sm" color="#4B5563" marginBottom={1}>
        {field.label}
      </Text>
      {isReactElement ? (
        <Box>{renderedValue}</Box>
      ) : (
        <Text fontWeight="medium">{String(renderedValue)}</Text>
      )}
    </Box>
  );
}

function DetailSectionComponent({ section }: { section: DetailSection }) {
  return (
    <Card.Root>
      <Card.Header>
        <Heading size="sm">{section.title}</Heading>
      </Card.Header>
      <Card.Body>
        <SimpleGrid columns={section.columns || 2} spacing="16px">
          {section.fields.map((field, index) => (
            <DetailFieldComponent
              key={`${section.id}-${index}`}
              field={field}
              value={field.value}
            />
          ))}
        </SimpleGrid>
      </Card.Body>
    </Card.Root>
  );
}

function TimelineComponent({ events, title }: { events: TimelineEvent[]; title?: string }) {
  return (
    <Card.Root>
      {title && (
        <Card.Header>
          <Heading size="sm">{title}</Heading>
        </Card.Header>
      )}
      <Card.Body>
        <Stack gap="16px">
          {events.map((event, index) => (
            <Flex key={event.id} gap={3}>
              <Box position="relative">
                <Box
                  width={10}
                  height={10}
                  borderRadius="full"
                  backgroundColor={event.color || 'blue.500'}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="white"
                >
                  {event.icon && (
                    <Icon as={event.icon} width="16px" height="16px">
                      {React.createElement(event.icon)}
                    </Icon>
                  )}
                </Box>
                {index < events.length - 1 && (
                  <Box
                    position="absolute"
                    left="50%"
                    top={10}
                    width="2px"
                    height="calc(100% + 16px)"
                    backgroundColor="#E5E7EB"
                    transform="translateX(-50%)"
                  />
                )}
              </Box>
              <Box flex={1}>
                <Flex justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Text fontWeight="medium">{event.title}</Text>
                    {event.description && (
                      <Text fontSize="sm" color="#4B5563" marginTop={1}>
                        {event.description}
                      </Text>
                    )}
                    {event.user && (
                      <Flex alignItems="center" gap={1} marginTop={1}>
                        <Icon as={FiUser} width="12px" height="12px" color="#6B7280">
                          <FiUser />
                        </Icon>
                        <Text fontSize="xs" color="#6B7280">
                          {event.user.name}
                        </Text>
                      </Flex>
                    )}
                  </Box>
                  <Text fontSize="xs" color="#6B7280">
                    {formatDate(new Date(event.timestamp), 'tr-TR', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                </Flex>
              </Box>
            </Flex>
          ))}
        </Stack>
      </Card.Body>
    </Card.Root>
  );
}

function RelatedItemsComponent({ items, title }: { items: RelatedItem[]; title?: string }) {
  return (
    <Card.Root>
      {title && (
        <Card.Header>
          <Heading size="sm">{title}</Heading>
        </Card.Header>
      )}
      <Card.Body>
        <Stack gap="8px">
          {items.map((item) => (
            <Flex
              key={item.id}
              alignItems="center"
              justifyContent="space-between"
              padding={2}
              borderRadius="md"
              cursor={item.onClick || item.href ? 'pointer' : 'default'}
              _hover={item.onClick || item.href ? { backgroundColor: '#F9FAFB' } : undefined}
              onClick={item.onClick}
            >
              <Flex alignItems="center" gap={3}>
                {item.icon && (
                  <Icon as={item.icon} color="#6B7280" width="16px" height="16px">
                    {React.createElement(item.icon)}
                  </Icon>
                )}
                <Box>
                  <Text fontSize="sm" fontWeight="medium">
                    {item.title}
                  </Text>
                  {item.subtitle && (
                    <Text fontSize="xs" color="#4B5563">
                      {item.subtitle}
                    </Text>
                  )}
                </Box>
              </Flex>
              {item.badge && <Badge size="sm">{item.badge}</Badge>}
            </Flex>
          ))}
        </Stack>
      </Card.Body>
    </Card.Root>
  );
}

export function ResourceDetail({
  // Resource data
  data,
  isLoading = false,
  error = null,

  // Resource info
  resourceName,
  title,
  subtitle,

  // Status
  status,

  // Metadata
  metadata,

  // Main content sections
  sections = [],

  // Tabs
  tabs = [],
  defaultTab,

  // Timeline
  timeline = [],
  timelineTitle = 'Activity',

  // Related items
  relatedItems = [],
  relatedTitle = 'Related',

  // Actions
  onEdit,
  onDelete,
  onPrint,
  onShare,
  onRefresh,
  customActions = [],

  // Quick edit
  // enableQuickEdit = false,
  // quickEditFields = [],
  // onQuickEdit,

  // Layout
  showHeader = true,
  showSidebar = true,
  // sidebarPosition = 'right',
}: ResourceDetailProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id || '');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const onDeleteOpen = () => setIsDeleteOpen(true);
  const onDeleteClose = () => setIsDeleteOpen(false);

  if (isLoading) {
    return <LoadingState text={`Loading ${resourceName}...`} />;
  }

  if (error) {
    return (
      <EmptyState
        icon={<FiRefreshCw />}
        title="Error loading data"
        description={error.message || `Failed to load ${resourceName}`}
        onAction={onRefresh}
      />
    );
  }

  if (!data) {
    return (
      <EmptyState
        title={`${resourceName} not found`}
        description="The requested resource could not be found."
      />
    );
  }

  const mainContent = (
    <Box flex={1}>
      {/* Header */}
      {showHeader && (
        <Box marginBottom={6}>
          <Flex justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Flex alignItems="center" gap={3}>
                <Heading size="lg">{title || `${resourceName} Details`}</Heading>
                {status && <Badge size="lg">{status.label}</Badge>}
              </Flex>
              {subtitle && (
                <Text color="#4B5563" marginTop={1}>
                  {subtitle}
                </Text>
              )}
            </Box>

            <Flex gap={2}>
              {/* Primary actions */}
              {onEdit && (
                <Button onClick={onEdit} leftIcon={<FiEdit2 />} size="sm">
                  Edit
                </Button>
              )}

              {/* More actions */}
              {(onPrint || onShare || customActions.length > 0 || onDelete) && (
                <Flex gap="8px">
                  {onPrint && (
                    <IconButton
                      icon={<FiPrinter />}
                      variant="ghost"
                      size="sm"
                      aria-label="Print"
                      onClick={onPrint}
                    />
                  )}
                  {onShare && (
                    <IconButton
                      icon={<FiShare2 />}
                      variant="ghost"
                      size="sm"
                      aria-label="Share"
                      onClick={onShare}
                    />
                  )}
                  {customActions.map((action, index) => (
                    <Button
                      key={index}
                      onClick={action.onClick}
                      leftIcon={action.icon ? React.createElement(action.icon) : undefined}
                      variant={action.variant || 'ghost'}
                      colorPalette={action.colorScheme}
                      size="sm"
                    >
                      {action.label}
                    </Button>
                  ))}
                  {onDelete && (
                    <IconButton
                      icon={<FiTrash2 />}
                      variant="ghost"
                      colorPalette="red"
                      size="sm"
                      aria-label="Delete"
                      onClick={onDeleteOpen}
                    />
                  )}
                </Flex>
              )}
            </Flex>
          </Flex>
        </Box>
      )}

      {/* Metadata section */}
      {metadata && (
        <Box marginBottom={6}>
          <DetailSectionComponent section={metadata} />
        </Box>
      )}

      {/* Main content - tabs or sections */}
      {tabs.length > 0 ? (
        <Box>
          <Flex borderBottom="1px solid #E5E7EB" marginBottom="24px">
            {tabs.map((tab) => (
              <Box
                key={tab.id}
                as="button"
                borderRadius="0"
                borderBottomWidth="2px"
                borderBottomColor={activeTab === tab.id ? 'blue.500' : 'transparent'}
                marginBottom="-1px"
                padding="8px 16px"
                cursor="pointer"
                backgroundColor={activeTab === tab.id ? 'gray.50' : 'transparent'}
                _hover={{ backgroundColor: 'gray.100' }}
                onClick={() => setActiveTab(tab.id)}
              >
                <Flex alignItems="center" gap={2}>
                  <Text>{tab.label}</Text>
                  {tab.badge && <Badge size="sm">{tab.badge}</Badge>}
                </Flex>
              </Box>
            ))}
          </Flex>
          <Box>{tabs.find((tab) => tab.id === activeTab)?.content}</Box>
        </Box>
      ) : (
        <Stack gap="24px">
          {sections.map((section) => (
            <DetailSectionComponent key={section.id} section={section} />
          ))}
        </Stack>
      )}
    </Box>
  );

  const sidebarContent = showSidebar && (timeline.length > 0 || relatedItems.length > 0) && (
    <Box width="100%">
      <Stack gap="24px">
        {timeline.length > 0 && <TimelineComponent events={timeline} title={timelineTitle} />}
        {relatedItems.length > 0 && (
          <RelatedItemsComponent items={relatedItems} title={relatedTitle} />
        )}
      </Stack>
    </Box>
  );

  return (
    <>
      <Flex gap="24px" direction="column">
        {mainContent}
        {sidebarContent}
      </Flex>

      {/* Delete confirmation dialog */}
      <Dialog.Root
        open={isDeleteOpen}
        onOpenChange={(e: { open: boolean }) => (e.open ? onDeleteOpen() : onDeleteClose())}
        data-testid="dialog"
      >
        <Dialog.Content>
          <Dialog.Header>
            Delete {resourceName}
            <Dialog.CloseTrigger data-testid="dialog-close" />
          </Dialog.Header>
          <Dialog.Body>
            <Text>
              Are you sure you want to delete this {resourceName.toLowerCase()}? This action cannot
              be undone.
            </Text>
          </Dialog.Body>
          <Dialog.Footer>
            <Flex gap="8px">
              <Button variant="ghost" onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorPalette="red"
                onClick={() => {
                  onDelete?.();
                  onDeleteClose();
                }}
              >
                Delete
              </Button>
            </Flex>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
